import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { Activity, Check, Users, Swords, CalendarDays, UserPlus, UserCheck, X, LogOut as LogOutIcon, ExternalLink, Bell, Trophy } from 'lucide-react'
import Header from '@/components/layout/Header'
import SteamLinkBanner from '@/components/SteamLinkBanner'
import { useAuth, isOperator } from '@/contexts/AuthContext'
import { PersonalActivity, TeamActivity, useActivitySummary } from '@/components/dashboard/ActivityCards'
import { useLanguage } from '@/contexts/LanguageContext'
import { roleOf, useRosterCatalog } from '@/lib/rosterCatalog'
import EmojiGlyph from '@/components/ui/EmojiGlyph'

/* ── Online user type ──────────────────────────────────────────────────────── */

interface OnlineUser {
  id: string
  name: string
  avatar: string
}


interface MemberCount {
  total: number
  tll: number
  tlla: number
  discord_online: number
  inactive: number
}

/* Newest arrivals in the *clan* — whoever most recently took the Üye or Akademi
   role. Not the Discord's own join date: people sit in the server for weeks
   before they are let in. `approx` marks a row whose date predates the records
   we keep and was estimated rather than observed. */
interface RecentJoin {
  id: string
  name: string
  avatar_url: string
  joined_at: number
  team: string
  approx: boolean
}

interface DashSignUp {
  user_id: string
  class_name: string
  is_active: boolean
}

interface EventData {
  id: string
  title: string
  start_time?: number
  next_start_time?: number
  channel_name?: string
  active_count: number
  sign_ups: DashSignUp[]
}

interface MatchData {
  event_id: string
  event: {
    id: string
    title: string
    start_time?: number
    active_count: number
    sign_ups: DashSignUp[]
    is_external?: boolean
    discord_url?: string
  }
}

interface RhSpec  { name: string; emoteId?: string; emoji?: string }
/** `last_spec`: the sub-class this user picked last time for the class (dashboard matches). */
interface RhClass { name: string; type: string; emoteId?: string; specs: RhSpec[]; last_spec?: string | null }

interface DashNotification {
  id: number
  event_id: string
  event_title: string
  squad_name: string | null
  role: string | null
  change_type?: string | null  // added | moved | removed (for roster_change)
  start_time?: number
  notif_type: 'roster_posted' | 'new_match' | 'roster_change' | 'strategy_posted' | 'signup_reminder'
  strategy_name?: string | null
  created_at?: number
}

const NON_ACTIVE = new Set(['Bench', 'Tentative', 'Absence', 'Late'])

const DAY_EN = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MONTH_EN = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const DAY_TR = ['Paz', 'Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt']
const MONTH_TR = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara']

function PendingCount({ signUps }: { signUps: DashSignUp[] }) {
  const n = signUps.filter(s => s.class_name === 'Bench' || s.class_name === 'Tentative').length
  return n > 0 ? <span className="opacity-50"> (+{n})</span> : null
}

/* Kept level with the COMP card sitting beside it: the grid row stretches both
   to the taller one, so a longer list would leave the stat block padded out. */
const RECENT_JOINS_SHOWN = 5

const sectionHeading =
  'mb-3 flex items-center gap-1.5 font-display text-[11px] font-bold uppercase tracking-[0.18em] text-foreground/70'

const DiscordIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
  </svg>
)

/* ── Inline sign-up widget for dashboard cards ─────────────────────────────── */

function QuickSignUp({ eventId, signupBase, isSignedUp, onOptimistic, onRefresh }: {
  eventId: string
  signupBase: 'events' | 'matches'
  isSignedUp: boolean
  onOptimistic: (signedUp: boolean, className?: string) => void
  onRefresh: () => void
}) {
  const { T } = useLanguage()
  const [open, setOpen] = useState(false)
  const [classes, setClasses] = useState<RhClass[]>([])
  const [loading, setLoading] = useState(false)
  const [busy, setBusy] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [selectedClass, setSelectedClass] = useState('')
  const [selectedSpec, setSelectedSpec] = useState('')
  const popupRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const [coords, setCoords] = useState<{ top: number; right: number } | null>(null)

  // Konum butonun ekrandaki yerinden hesaplanır: menü portal ile body'ye
  // çizildiği için kartların backdrop-blur stacking context'lerinin altında
  // kalmaz. Kaydırma/yeniden boyutlamada tazelenir.
  useEffect(() => {
    if (!open) return
    const reposition = () => {
      const r = triggerRef.current?.getBoundingClientRect()
      if (r) setCoords({ top: r.bottom + 4, right: window.innerWidth - r.right })
    }
    reposition()
    window.addEventListener('resize', reposition)
    window.addEventListener('scroll', reposition, true)
    return () => {
      window.removeEventListener('resize', reposition)
      window.removeEventListener('scroll', reposition, true)
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      const t = e.target as Node
      if (popupRef.current?.contains(t) || triggerRef.current?.contains(t)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const loadClasses = async () => {
    if (classes.length > 0) { setOpen(true); return }
    setLoading(true)
    setOpen(true)
    try {
      const r = await fetch(`/api/${signupBase}/${eventId}/classes`, { credentials: 'include' })
      if (r.ok) {
        const d = await r.json()
        setClasses(d.classes ?? [])
      }
    } finally { setLoading(false) }
  }

  const handleSignUp = async () => {
    if (!selectedClass) return
    setBusy(true)
    // Optimistic: instantly reflect sign-up in UI
    onOptimistic(true, selectedClass)
    setOpen(false)
    const cls = selectedClass
    const spec = selectedSpec
    setSelectedClass('')
    setSelectedSpec('')
    try {
      const res = await fetch(`/api/${signupBase}/${eventId}/signup`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ class_name: cls, spec_name: spec || undefined }),
      })
      if (!res.ok) onOptimistic(false) // revert on failure
      // Background refresh to sync real data
      setTimeout(onRefresh, 5000)
    } catch {
      onOptimistic(false) // revert on network error
    } finally { setBusy(false) }
  }

  const handleCancel = async () => {
    setBusy(true)
    // Optimistic: instantly reflect cancellation in UI
    onOptimistic(false)
    setConfirming(false)
    try {
      const res = await fetch(`/api/${signupBase}/${eventId}/signup`, {
        method: 'DELETE', credentials: 'include',
      })
      if (!res.ok) onOptimistic(true) // revert on failure
      setTimeout(onRefresh, 5000)
    } catch {
      onOptimistic(true) // revert on network error
    } finally { setBusy(false) }
  }

  if (isSignedUp) {
    if (confirming) {
      return (
        <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
          <span className="text-[10px] text-muted-foreground mr-0.5">{T.dashboard.areYouSure}</span>
          <button
            onClick={handleCancel}
            disabled={busy}
            className="px-1.5 py-0.5 rounded text-[10px] font-medium border border-red-500/30 text-red-400 bg-red-500/10 hover:bg-red-500/20 transition-all disabled:opacity-40"
          >
            {T.dashboard.yes}
          </button>
          <button
            onClick={() => setConfirming(false)}
            className="px-1.5 py-0.5 rounded text-[10px] font-medium border border-white/10 text-muted-foreground bg-white/5 hover:bg-white/10 transition-all"
          >
            {T.dashboard.no}
          </button>
        </div>
      )
    }
    return (
      <button
        onClick={e => { e.stopPropagation(); setConfirming(true) }}
        disabled={busy}
        className="flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium border border-red-500/25 text-red-400/70 bg-red-500/8 hover:bg-red-500/15 hover:text-red-400 transition-all disabled:opacity-40"
      >
        <LogOutIcon className="h-3 w-3" />
        {T.dashboard.cancelSignUp}
      </button>
    )
  }

  const primaryClasses = classes.filter(c => c.type === 'primary')
  const extraClasses = classes.filter(c => c.type === 'default' && ['Bench', 'Tentative', 'Absence'].includes(c.name))
  const allOptions = [...primaryClasses, ...extraClasses]
  const selectedObj = allOptions.find(c => c.name === selectedClass)
  const specs = selectedObj?.specs ?? []

  return (
    <div className="relative" onClick={e => e.stopPropagation()}>
      <button
        ref={triggerRef}
        onClick={loadClasses}
        className="flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium border border-primary/30 text-primary/80 bg-primary/8 hover:bg-primary/15 hover:text-primary transition-all"
      >
        <UserPlus className="h-3 w-3" />
        {T.dashboard.signUp}
      </button>

      {open && coords && createPortal(
        <div
          ref={popupRef}
          onClick={e => e.stopPropagation()}
          style={{ position: 'fixed', top: coords.top, right: coords.right }}
          className="z-50 w-56 rounded-lg bg-card border border-white/15 shadow-xl p-3 flex flex-col gap-2"
        >
          <div className="flex items-center justify-between">
            <p className="text-[10px] font-semibold tracking-wider text-muted-foreground">{T.dashboard.chooseClass}</p>
            <button onClick={() => setOpen(false)} className="text-muted-foreground hover:text-foreground">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
          {loading ? (
            <div className="flex gap-1.5">
              {[48, 40, 52, 44].map((w, i) => (
                <div key={i} style={{ width: w }} className="h-[22px] rounded bg-white/5 animate-pulse" />
              ))}
            </div>
          ) : (
            <>
              <div className="flex flex-wrap gap-1">
                {allOptions.map(c => (
                  <button
                    key={c.name}
                    onClick={() => { setSelectedClass(c.name); setSelectedSpec(c.last_spec ?? '') }}
                    className={`px-1.5 py-0.5 rounded text-[10px] font-medium border transition-all ${
                      selectedClass === c.name
                        ? 'bg-primary/20 border-primary/50 text-primary'
                        : 'bg-white/5 border-white/10 text-muted-foreground hover:bg-white/10'
                    }`}
                  >
                    {c.name}
                  </button>
                ))}
              </div>
              {specs.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {specs.map(s => (
                    <button
                      key={s.name}
                      // Optional: clicking the picked one again clears it.
                      onClick={() => setSelectedSpec(cur => (cur === s.name ? '' : s.name))}
                      className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium border transition-all ${
                        selectedSpec === s.name
                          ? 'bg-primary/20 border-primary/50 text-primary'
                          : 'bg-white/5 border-white/10 text-muted-foreground hover:bg-white/10'
                      }`}
                    >
                      {s.emoji && <EmojiGlyph emoji={s.emoji} size={11} />}
                      {s.name}
                    </button>
                  ))}
                </div>
              )}
              <button
                onClick={handleSignUp}
                disabled={!selectedClass || busy}
                className="w-full flex items-center justify-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium border border-primary/30 text-primary bg-primary/10 hover:bg-primary/20 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <UserPlus className="h-3 w-3" />
                {T.dashboard.confirm}
              </button>
            </>
          )}
        </div>,
        document.body,
      )}
    </div>
  )
}

/* ── Online topbar with overflow tooltip ────────────────────────────────────── */

function OnlineTopbar({ users }: { users: OnlineUser[] }) {
  const { T } = useLanguage()
  const wrapperRef = useRef<HTMLDivElement>(null)
  const badgeRef = useRef<HTMLSpanElement>(null)
  const [hiddenUsers, setHiddenUsers] = useState<OnlineUser[]>([])
  const [showTooltip, setShowTooltip] = useState(false)

  useEffect(() => {
    const wrapper = wrapperRef.current
    if (!wrapper) return

    const measure = () => {
      const badge = badgeRef.current
      if (!badge) return

      // Get the right edge of the wrapper (the clipping boundary)
      const wrapperRight = wrapper.getBoundingClientRect().right
      // Get all user item elements
      const items = wrapper.querySelectorAll<HTMLElement>('[data-user-item]')
      if (items.length === 0) {
        badge.style.display = 'none'
        setHiddenUsers([])
        return
      }

      // First pass: how many fit if we need a badge?
      // Badge is ~80px, but measure it properly
      badge.style.display = 'none' // hide badge to measure items cleanly
      const badgeGap = 12 // gap-3 = 12px

      // Check if ALL items fit without badge
      const lastItem = items[items.length - 1]
      const allFit = lastItem.getBoundingClientRect().right <= wrapperRight + 1 // 1px tolerance

      if (allFit) {
        badge.style.display = 'none'
        setHiddenUsers([])
        return
      }

      // Not all fit — show badge and figure out how many are visible
      badge.style.display = ''
      const badgeWidth = badge.offsetWidth + badgeGap
      const cutoff = wrapperRight - badgeWidth

      let visCount = 0
      for (const item of items) {
        if (item.getBoundingClientRect().right <= cutoff + 1) visCount++
        else break
      }

      // Ensure at least 0
      if (visCount < 0) visCount = 0

      setHiddenUsers(users.slice(visCount))
    }

    // Use ResizeObserver on the wrapper's parent (the flex row) to avoid
    // feedback loops — the wrapper itself doesn't change size since it's
    // flex-1 with overflow hidden.
    const target = wrapper.parentElement ?? wrapper
    const ro = new ResizeObserver(() => {
      requestAnimationFrame(measure)
    })
    ro.observe(target)

    // Initial measure
    requestAnimationFrame(measure)

    return () => ro.disconnect()
  }, [users])

  return (
    <div className="border-b border-white/10 bg-black/20 px-5 py-2.5 backdrop-blur-sm">
      <div className="flex items-center gap-3">
        <h3 className="flex shrink-0 items-center gap-1.5 font-display text-[11px] font-bold uppercase tracking-[0.18em] text-foreground/70">
          {T.dashboard.online}
          <span className="inline-flex h-4 min-w-4 px-1 items-center justify-center rounded-full bg-green-500/20 text-[9px] font-bold text-green-400">
            {users.length}
          </span>
        </h3>
        <div ref={wrapperRef} className="flex items-center gap-2 flex-1 min-w-0 overflow-hidden py-0.5">
          {users.length === 0 && (
            <p className="text-[11px] text-muted-foreground/40">{T.dashboard.noUsersOnline}</p>
          )}
          {users.map(u => (
            <div
              key={u.id}
              data-user-item
              className="flex items-center gap-1.5 shrink-0"
              title={u.name}
            >
              <span className="relative shrink-0">
                <img
                  src={u.avatar}
                  alt={u.name}
                  className="h-7 w-7 rounded-full object-cover"
                  onError={e => { (e.target as HTMLImageElement).src = 'https://cdn.discordapp.com/embed/avatars/0.png' }}
                />
                <span className="absolute bottom-0 right-0 h-2 w-2 rounded-full bg-green-500 ring-1 ring-background" />
              </span>
              <span className="text-xs font-medium truncate max-w-[140px]">{u.name}</span>
            </div>
          ))}
        </div>
        <div
          className="relative shrink-0"
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
        >
          <span
            ref={badgeRef}
            className="text-[11px] font-medium text-muted-foreground/60 hover:text-muted-foreground cursor-default transition-colors whitespace-nowrap"
            style={{ display: hiddenUsers.length > 0 ? '' : 'none' }}
          >
            {T.dashboard.others.replace('{n}', String(hiddenUsers.length))}
          </span>
          {showTooltip && hiddenUsers.length > 0 && (
            <div className="absolute right-0 top-full mt-1.5 z-30 w-48 rounded-lg bg-card border border-white/15 shadow-xl p-2.5 flex flex-col gap-1.5">
              {hiddenUsers.map(u => (
                <div key={u.id} className="flex items-center gap-2">
                  <span className="relative shrink-0">
                    <img
                      src={u.avatar}
                      alt={u.name}
                      className="h-5 w-5 rounded-full object-cover"
                      onError={e => { (e.target as HTMLImageElement).src = 'https://cdn.discordapp.com/embed/avatars/0.png' }}
                    />
                    <span className="absolute -bottom-0.5 -right-0.5 h-1.5 w-1.5 rounded-full bg-green-500 ring-1 ring-background" />
                  </span>
                  <span className="text-xs font-medium truncate">{u.name}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

/* ── Canvas wrapper for sections ───────────────────────────────────────────── */

function SectionCanvas({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`rounded-md border border-white/10 bg-black/25 p-4 backdrop-blur-sm ${className}`}>
      {children}
    </div>
  )
}

/* ── Last painted state, kept for the length of the session ────────────────── */

/* Leaving the panel and coming back is the commonest way to open it, and until
   now it cost a full set of skeletons every time: the state was born empty and
   four requests had to land before anything real appeared. The numbers were on
   screen a second earlier and none of them changed in the meantime.

   So the last answer is kept in module scope and used as the initial state.
   The fetches still run — this is a first paint, not a cache — but the panel
   opens with what it last knew rather than with placeholders. Cleared on a
   reload, which is the right lifetime: it is a render shortcut, not storage. */
const snapshot: {
  memberCount: MemberCount | null
  recentJoins: RecentJoin[] | null
  events: EventData[]
  matches: MatchData[]
  onlineUsers: OnlineUser[]
  notifications: DashNotification[]
} = {
  memberCount: null,
  recentJoins: null,
  events: [],
  matches: [],
  onlineUsers: [],
  notifications: [],
}

/* ── Main Dashboard ────────────────────────────────────────────────────────── */

export default function Dashboard() {
  const { user } = useAuth()
  const { T, lang } = useLanguage()
  const rosterCatalog = useRosterCatalog()
  const activity = useActivitySummary()
  const navigate = useNavigate()

  const DAY = lang === 'tr' ? DAY_TR : DAY_EN
  const MONTH = lang === 'tr' ? MONTH_TR : MONTH_EN

  const timeAgo = (ts?: number): string => {
    if (!ts) return ''
    const now = Math.floor(Date.now() / 1000)
    const diff = now - ts
    if (diff < 60) return T.dashboard.justNow
    if (diff < 3600) return T.dashboard.mAgo.replace('{n}', String(Math.floor(diff / 60)))
    if (diff < 86400) return T.dashboard.hAgo.replace('{n}', String(Math.floor(diff / 3600)))
    return T.dashboard.dAgo.replace('{n}', String(Math.floor(diff / 86400)))
  }

  const formatEventDate = (ts?: number): string => {
    if (!ts) return '-'
    const d = new Date(ts * 1000)
    const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
    return `${DAY[d.getDay()]}, ${d.getDate()} ${MONTH[d.getMonth()]} · ${time}`
  }
  const [memberCount, setMemberCount] = useState<MemberCount | null>(snapshot.memberCount)
  const [recentJoins, setRecentJoins] = useState<RecentJoin[] | null>(snapshot.recentJoins)
  const [events, setEvents] = useState<EventData[]>(snapshot.events)
  const [matches, setMatches] = useState<MatchData[]>(snapshot.matches)
  const [onlineUsers, setOnlineUsers] = useState<OnlineUser[]>(snapshot.onlineUsers)
  const [loading, setLoading] = useState(snapshot.memberCount === null)
  const [matchesLoading, setMatchesLoading] = useState(snapshot.matches.length === 0)
  const [notifications, setNotifications] = useState<DashNotification[]>(snapshot.notifications)

  // Optimistic sign-up overrides: eventId -> { value, timestamp }
  const [signUpOverrides, setSignUpOverrides] = useState<Record<string, { value: boolean; at: number }>>({})

  /* Mirror into the snapshot on every render rather than inside each setter:
     a dismissed notification and an optimistic sign-up change the state too,
     and a snapshot that only tracked the fetches would hand them back on the
     way in. */
  useEffect(() => {
    snapshot.memberCount = memberCount
    snapshot.recentJoins = recentJoins
    snapshot.events = events
    snapshot.matches = matches
    snapshot.onlineUsers = onlineUsers
    snapshot.notifications = notifications
  }, [memberCount, recentJoins, events, matches, onlineUsers, notifications])

  const fetchAll = useCallback(async () => {
    const safeFetch = (url: string) =>
      fetch(url, { credentials: 'include' }).then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        return r.json()
      })

    const [countRes, eventsRes, onlineRes, joinsRes] = await Promise.allSettled([
      safeFetch('/api/members/count'),
      safeFetch('/api/events'),
      safeFetch('/api/online'),
      safeFetch(`/api/members/recent-joins?limit=${RECENT_JOINS_SHOWN}`),
    ])
    // A rejected leg leaves its card showing what it had — a 503 from the
    // roster means "ask again", not "the clan is empty".
    if (countRes.status === 'fulfilled') setMemberCount(countRes.value)
    if (joinsRes.status === 'fulfilled') setRecentJoins(joinsRes.value)
    if (eventsRes.status === 'fulfilled') {
      const val = eventsRes.value as EventData[]
      setEvents(prev => val.length > 0 || prev.length === 0 ? val : prev)
    }
    if (onlineRes.status === 'fulfilled') setOnlineUsers(onlineRes.value)
    setLoading(false)
  }, [])

  const fetchMatches = useCallback(async () => {
    try {
      const r = await fetch('/api/matches', { credentials: 'include' })
      if (r.ok) {
        const val = await r.json() as MatchData[]
        setMatches(prev => val.length > 0 || prev.length === 0 ? val : prev)
      }
    } catch { /* keep previous data on error */ }
    setMatchesLoading(false)
  }, [])

  useEffect(() => {
    fetchAll()
    fetchMatches()
    const interval = setInterval(() => {
      fetchAll()
      fetchMatches()
    }, 30_000)
    return () => clearInterval(interval)
  }, [fetchAll, fetchMatches])

  // Notifications — visual read state is local-only; server marks read only on dismiss (X)
  const [readNotifIds, setReadNotifIds] = useState<Set<number>>(() => {
    try {
      const stored = localStorage.getItem('tll_read_notifs')
      return stored ? new Set(JSON.parse(stored) as number[]) : new Set()
    } catch { return new Set() }
  })
  const dismissedNotifIds = useRef<Set<number>>(new Set())

  // Persist read state to localStorage so it survives tab switches
  useEffect(() => {
    localStorage.setItem('tll_read_notifs', JSON.stringify([...readNotifIds]))
  }, [readNotifIds])

  const fetchNotifications = useCallback(() => {
    if (!user) return
    fetch('/api/notifications', { credentials: 'include' })
      .then(r => r.ok ? r.json() : [])
      .then((serverNotifs: DashNotification[]) => {
        setNotifications(serverNotifs.filter(n => !dismissedNotifIds.current.has(n.id)))
        // Clean up read IDs for notifications that no longer exist
        const serverIds = new Set(serverNotifs.map((n: DashNotification) => n.id))
        setReadNotifIds(prev => {
          const cleaned = new Set([...prev].filter(id => serverIds.has(id)))
          return cleaned.size !== prev.size ? cleaned : prev
        })
      })
      .catch(() => {})
  }, [user])

  useEffect(() => {
    // Fetch notifications after a short delay to let fetchAll trigger detect_new_matches on server
    const initialTimer = setTimeout(fetchNotifications, 1500)
    const id = setInterval(fetchNotifications, 30_000)
    return () => { clearTimeout(initialTimer); clearInterval(id) }
  }, [fetchNotifications])

  // Also re-fetch notifications after dashboard data loads (so new match detection has run)
  const fetchAllDone = useRef(false)
  useEffect(() => {
    if (!loading && !fetchAllDone.current) {
      fetchAllDone.current = true
      // fetchAll completed → detect_new_matches ran on server → fetch fresh notifications
      setTimeout(fetchNotifications, 500)
    }
  }, [loading, fetchNotifications])

  const handleMarkRead = (notifId: number) => {
    if (readNotifIds.has(notifId)) return
    setReadNotifIds(prev => new Set(prev).add(notifId))
    // No server call — visual-only until user clicks X
  }

  const handleRemoveNotif = async (notifId: number) => {
    dismissedNotifIds.current.add(notifId)
    setNotifications(prev => prev.filter(n => n.id !== notifId))
    setReadNotifIds(prev => { const s = new Set(prev); s.delete(notifId); return s })
    try {
      await fetch('/api/notifications/read', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notification_id: notifId }),
      })
    } catch { /* will disappear on next poll anyway */ }
  }

  const optimisticSignUp = useCallback((eventId: string, signedUp: boolean, className?: string) => {
    setSignUpOverrides(prev => ({ ...prev, [eventId]: { value: signedUp, at: Date.now() } }))
    // Also optimistically update counts in local state
    if (user) {
      const isActive = className ? !NON_ACTIVE.has(className) : false
      setEvents(prev => prev.map(e => {
        if (e.id !== eventId) return e
        const without = e.sign_ups.filter(s => s.user_id !== user.id)
        const newSignUps = signedUp && className
          ? [...without, { user_id: user.id, class_name: className, is_active: isActive }]
          : without
        return {
          ...e,
          sign_ups: newSignUps,
          active_count: newSignUps.filter(s => s.is_active && !NON_ACTIVE.has(s.class_name)).length,
        }
      }))
      setMatches(prev => prev.map(m => {
        if (m.event_id !== eventId) return m
        const without = m.event.sign_ups.filter(s => s.user_id !== user.id)
        const newSignUps = signedUp && className
          ? [...without, { user_id: user.id, class_name: className, is_active: isActive }]
          : without
        return {
          ...m,
          event: {
            ...m.event,
            sign_ups: newSignUps,
            active_count: newSignUps.filter(s => s.is_active && !NON_ACTIVE.has(s.class_name)).length,
          },
        }
      }))
    }
  }, [user])

  // Resolve sign-up status: optimistic override takes precedence for 60s or until server confirms
  const isSignedUp = useCallback((eventId: string, signUps: DashSignUp[]) => {
    const serverSays = !!user && signUps.some(s => s.user_id === user.id)
    const override = signUpOverrides[eventId]
    if (override) {
      const age = Date.now() - override.at
      // Only clear override if server agrees AND at least 10s have passed (to survive intermediate fetches)
      if (override.value === serverSays && age > 10_000) {
        setSignUpOverrides(prev => {
          const next = { ...prev }
          delete next[eventId]
          return next
        })
        return serverSays
      }
      // Keep override alive for up to 60s even if server disagrees (Raid Helper sync is slow)
      if (age < 60_000) return override.value
      // After 60s, trust server
      setSignUpOverrides(prev => {
        const next = { ...prev }
        delete next[eventId]
        return next
      })
    }
    return serverSays
  }, [user, signUpOverrides])

  const upcomingEvents = [...events].sort(
    (a, b) => (a.next_start_time ?? a.start_time ?? 0) - (b.next_start_time ?? b.start_time ?? 0)
  )

  const now = Math.floor(Date.now() / 1000)
  const upcomingMatches = matches
    .filter(m => !m.event.start_time || m.event.start_time >= now)
    .sort((a, b) => (a.event.start_time ?? 0) - (b.event.start_time ?? 0))

  return (
    <div className="flex h-full">
      {/* ── Main column: sabit başlık + kaydırılabilir gövde ──────────────────── */}
      {/* Header scroll alanının dışında (diğer tüm sayfalardaki gibi): aşağı
          kaydırınca üst çubuk ve hamburger menü yerinde kalır. */}
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <Header title={T.dashboard.title} />
        <div className="flex-1 overflow-y-auto">
          <SteamLinkBanner />

          {/* ── Online topbar (sidebar-style blur) ─────────────────────────────── */}
          <OnlineTopbar users={onlineUsers} />

          <div className="p-5">
          {/* ── 2-column grid ─────────────────────────────────────────────────── */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

            {/* COMP — 2x2 stat cards */}
            <SectionCanvas>
              <h3 className={sectionHeading}>
                <Trophy className="h-3.5 w-3.5 shrink-0 text-primary" />
                {T.dashboard.comp}
              </h3>
              <div className="grid grid-cols-2 gap-2.5">
                {memberCount ? (
                  <>
                    <div className="rounded-sm border border-white/10 bg-white/[0.035] px-4 py-3">
                      <div className="flex items-center justify-between mb-1.5">
                        <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{T.dashboard.totalMembers}</p>
                        <img src="/roles/infantry.webp" alt="" className="h-4 w-4 opacity-60" />
                      </div>
                      <p className="font-display text-3xl font-bold leading-none tabular-nums">{memberCount.total}</p>
                    </div>
                    <div className="rounded-sm border border-white/10 bg-white/[0.035] px-4 py-3">
                      <div className="flex items-center justify-between mb-1.5">
                        <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{T.dashboard.mainTeam}</p>
                        <span className="text-[10px] font-bold text-primary">TLL</span>
                      </div>
                      <p className="font-display text-3xl font-bold leading-none tabular-nums">{memberCount.tll}</p>
                    </div>
                    <div className="rounded-sm border border-white/10 bg-white/[0.035] px-4 py-3">
                      <div className="flex items-center justify-between mb-1.5">
                        <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{T.dashboard.academy}</p>
                        <span className="text-[10px] font-bold text-sky-300">TLLA</span>
                      </div>
                      <p className="font-display text-3xl font-bold leading-none tabular-nums">{memberCount.tlla}</p>
                    </div>
                    <div className="rounded-sm border border-white/10 bg-white/[0.035] px-4 py-3">
                      <div className="flex items-center justify-between mb-1.5">
                        <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{T.dashboard.onlineInDiscord}</p>
                        <DiscordIcon className="h-3.5 w-3.5 text-[#5865F2]" />
                      </div>
                      <p className="font-display text-3xl font-bold leading-none tabular-nums">{memberCount.discord_online}</p>
                    </div>
                    {/* Tam genişlik: yukarıdaki dörtlünün parçası değil, toplama girmiyor. */}
                    <div className="col-span-2 flex items-center justify-between gap-3 rounded-sm border border-white/10 bg-white/[0.035] px-4 py-2.5">
                      <div className="min-w-0">
                        <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{T.dashboard.inactive}</p>
                        <p className="mt-0.5 truncate text-[11px] text-muted-foreground/60">{T.dashboard.inactiveHint}</p>
                      </div>
                      <p className="font-display text-2xl font-bold leading-none tabular-nums text-foreground/80">{memberCount.inactive}</p>
                    </div>
                  </>
                ) : (
                  <>
                    {Array.from({ length: 4 }).map((_, i) => (
                      <div key={i} className="rounded-sm border border-white/10 bg-white/[0.035] px-4 py-3 animate-pulse">
                        <div className="flex items-center justify-between mb-1.5">
                          <div className="h-3 w-20 rounded bg-white/8" />
                          <div className="h-3 w-3 rounded bg-white/8" />
                        </div>
                        <div className="h-8 w-12 rounded bg-white/8 mt-1" />
                      </div>
                    ))}
                    <div className="col-span-2 h-14 rounded-sm border border-white/10 bg-white/[0.035] animate-pulse" />
                  </>
                )}
              </div>
            </SectionCanvas>

            {/* Recent joins */}
            <SectionCanvas>
              <h3 className={sectionHeading}>
                <UserPlus className="h-3.5 w-3.5 shrink-0 text-primary" />
                {T.dashboard.recentJoins}
              </h3>
              {recentJoins === null ? (
                <div className="space-y-1.5">
                  {Array.from({ length: RECENT_JOINS_SHOWN }).map((_, i) => (
                    <div key={i} className="flex items-center gap-2.5 rounded-sm border border-white/10 bg-white/[0.035] px-3 py-2 animate-pulse">
                      <div className="h-7 w-7 shrink-0 rounded-full bg-white/8" />
                      <div className="flex-1 space-y-1.5">
                        <div className="h-3 w-1/2 rounded bg-white/8" />
                        <div className="h-2.5 w-14 rounded bg-white/6" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : recentJoins.length > 0 ? (
                <div className="space-y-1.5">
                  {recentJoins.map(j => (
                    <div
                      key={j.id}
                      className="flex items-center gap-2.5 rounded-sm border border-white/10 bg-white/[0.035] px-3 py-2"
                    >
                      <img
                        src={j.avatar_url || 'https://cdn.discordapp.com/embed/avatars/0.png'}
                        alt=""
                        className="h-7 w-7 shrink-0 rounded-full object-cover"
                        onError={e => { (e.target as HTMLImageElement).src = 'https://cdn.discordapp.com/embed/avatars/0.png' }}
                      />
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-display text-[13px] font-semibold tracking-tight">{j.name}</p>
                        <p className={`text-[10px] font-bold uppercase tracking-[0.14em] ${
                          j.team === 'TLL' ? 'text-primary' : 'text-sky-300'
                        }`}>
                          {j.team}
                        </p>
                      </div>
                      <span
                        className="shrink-0 text-[11px] tabular-nums text-muted-foreground/50"
                        title={j.approx ? T.dashboard.joinedApprox : undefined}
                      >
                        {j.approx && '~'}{timeAgo(j.joined_at)}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground/50 italic">{T.dashboard.noRecentJoins}</p>
              )}
            </SectionCanvas>

            {/* Activity — team and personal. Measured in answers, not in
                appearances: see ActivityCards.tsx. */}
            <SectionCanvas>
              <h3 className={sectionHeading} title={T.dashboard.activityHint}>
                <Activity className="h-3.5 w-3.5 shrink-0 text-primary" />
                {T.dashboard.teamActivity}
                {isOperator(user) && (
                  <button
                    onClick={() => navigate('/activity')}
                    className="ml-auto cursor-pointer text-[10px] font-semibold tracking-[0.14em] text-muted-foreground/60 transition-colors hover:text-foreground"
                  >
                    {T.dashboard.activityDetails}
                  </button>
                )}
              </h3>
              <TeamActivity data={activity} />
            </SectionCanvas>

            <SectionCanvas>
              <h3 className={sectionHeading} title={T.dashboard.activityHint}>
                <UserCheck className="h-3.5 w-3.5 shrink-0 text-primary" />
                {T.dashboard.personalActivity}
              </h3>
              <PersonalActivity data={activity} />
            </SectionCanvas>

            {/* Upcoming Matches */}
            <SectionCanvas>
              <h3 className={sectionHeading}>
                <Swords className="h-3.5 w-3.5 shrink-0 text-primary" />
                {T.dashboard.upcomingMatches}
              </h3>
              {matchesLoading && upcomingMatches.length === 0 ? (
                <div className="space-y-1.5">
                  {[0, 1].map(i => (
                    <div key={i} className="rounded-sm border border-white/10 bg-white/[0.035] px-3.5 py-2.5 animate-pulse">
                      <div className="flex items-center gap-3">
                        <div className="flex-1 space-y-1.5">
                          <div className="h-4 w-3/4 rounded bg-white/8" />
                          <div className="h-3 w-1/2 rounded bg-white/6" />
                          <div className="h-3 w-16 rounded bg-white/6" />
                        </div>
                        <div className="space-y-1.5">
                          <div className="h-3 w-16 rounded bg-white/6 ml-auto" />
                          <div className="h-6 w-14 rounded bg-white/6 ml-auto" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : upcomingMatches.length > 0 ? (
                <div className="space-y-1.5">
                  {upcomingMatches.map(match => {
                    const signedUp = isSignedUp(match.event_id, match.event.sign_ups)
                    const isExternal = match.event.is_external
                    const discordUrl = match.event.discord_url
                    const notSignedUp = !signedUp && !!user
                    return (
                      <div
                        key={match.event_id}
                        className="relative flex items-center gap-3 rounded-sm border border-white/10 bg-white/[0.035] px-3.5 py-2.5 cursor-pointer hover:bg-white/[0.07] transition-colors overflow-visible"
                        onClick={() => navigate('/matches')}
                      >
                        {notSignedUp && (
                          <div className="absolute inset-0 rounded-lg border-2 border-live/40 pointer-events-none animate-pulse-fade" />
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="truncate font-display text-[13px] font-semibold tracking-tight">{match.event.title}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {formatEventDate(match.event.start_time)}
                          </p>
                          <span className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
                            <Users className="h-3 w-3" />
                            {match.event.active_count}
                            <PendingCount signUps={match.event.sign_ups} />
                          </span>
                        </div>
                        {user && (
                          <div className="flex flex-col items-end gap-1 shrink-0">
                            {signedUp ? (
                              <span className="flex items-center gap-0.5 text-[11px] text-green-400/80 font-medium">
                                <Check className="h-3 w-3" />
                                {T.dashboard.signedUp}
                              </span>
                            ) : (
                              <span className="text-[11px] text-live/70 font-medium animate-pulse-fade">
                                {T.dashboard.notSignedUp}
                              </span>
                            )}
                            {isExternal && discordUrl ? (
                              <a
                                href={discordUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={e => e.stopPropagation()}
                                className="flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium border border-violet-500/30 text-violet-400/80 bg-violet-500/8 hover:bg-violet-500/15 hover:text-violet-300 transition-all"
                              >
                                <ExternalLink className="h-3 w-3" />
                                {T.dashboard.viewInDiscord}
                              </a>
                            ) : !isExternal ? (
                              <QuickSignUp
                                eventId={match.event_id}
                                signupBase="matches"
                                isSignedUp={signedUp}
                                onOptimistic={(su, cls) => optimisticSignUp(match.event_id, su, cls)}
                                onRefresh={fetchMatches}
                              />
                            ) : null}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground/50 italic">{T.dashboard.noUpcomingMatches}</p>
              )}
            </SectionCanvas>

            {/* Upcoming Events */}
            <SectionCanvas>
              <h3 className={sectionHeading}>
                <CalendarDays className="h-3.5 w-3.5 shrink-0 text-primary" />
                {T.dashboard.upcomingEvents}
              </h3>
              {loading && upcomingEvents.length === 0 ? (
                <div className="space-y-1.5">
                  {[0, 1, 2].map(i => (
                    <div key={i} className="rounded-sm border border-white/10 bg-white/[0.035] px-3.5 py-2.5 animate-pulse">
                      <div className="flex items-center gap-3">
                        <div className="flex-1 space-y-1.5">
                          <div className="h-4 w-3/4 rounded bg-white/8" />
                          <div className="h-3 w-1/2 rounded bg-white/6" />
                          <div className="h-3 w-16 rounded bg-white/6" />
                        </div>
                        <div className="space-y-1.5">
                          <div className="h-3 w-16 rounded bg-white/6 ml-auto" />
                          <div className="h-6 w-14 rounded bg-white/6 ml-auto" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : upcomingEvents.length > 0 ? (
                <div className="space-y-1.5">
                  {upcomingEvents.map(event => {
                    const signedUp = isSignedUp(event.id, event.sign_ups)
                    const notSignedUp = !signedUp && !!user
                    return (
                      <div
                        key={event.id}
                        className="relative flex items-center gap-3 rounded-sm border border-white/10 bg-white/[0.035] px-3.5 py-2.5 cursor-pointer hover:bg-white/[0.07] transition-colors overflow-visible"
                        onClick={() => navigate('/events')}
                      >
                        {notSignedUp && (
                          <div className="absolute inset-0 rounded-lg border-2 border-live/40 pointer-events-none animate-pulse-fade" />
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="truncate font-display text-[13px] font-semibold tracking-tight">{event.title}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {formatEventDate(event.start_time)}
                          </p>
                          <span className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
                            <Users className="h-3 w-3" />
                            {event.active_count}
                            <PendingCount signUps={event.sign_ups} />
                          </span>
                        </div>
                        {user && (
                          <div className="flex flex-col items-end gap-1 shrink-0">
                            {signedUp ? (
                              <span className="flex items-center gap-0.5 text-[11px] text-green-400/80 font-medium">
                                <Check className="h-3 w-3" />
                                {T.dashboard.signedUp}
                              </span>
                            ) : (
                              <span className="text-[11px] text-live/70 font-medium animate-pulse-fade">
                                {T.dashboard.notSignedUp}
                              </span>
                            )}
                            <QuickSignUp
                              eventId={event.id}
                              signupBase="events"
                              isSignedUp={signedUp}
                              onOptimistic={(su, cls) => optimisticSignUp(event.id, su, cls)}
                              onRefresh={fetchAll}
                            />
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground/50 italic">{T.dashboard.noUpcomingEvents}</p>
              )}
            </SectionCanvas>

          </div>
        </div>
        </div>
      </div>

      {/* ── Notifications sidebar (right, only on Dashboard) ──────────────────── */}
      <aside className="hidden h-full w-64 shrink-0 flex-col border-l border-white/10 bg-black/25 backdrop-blur-sm xl:flex">
        {/* Sayfa başlığıyla aynı 68px: panelin üst satırı sidebar'dan bildirim
            sütununun sonuna kadar tek hat. */}
        <div className="flex h-[68px] shrink-0 items-center border-b border-white/10 px-4">
          <h2 className="flex items-center gap-1.5 font-display text-[11px] font-bold uppercase tracking-[0.18em] text-foreground/70">
            <Bell className="h-3.5 w-3.5 shrink-0 text-primary" />
            {T.dashboard.notifications}
            {notifications.length > 0 && (
              <span className="ml-1 text-[11px] text-live font-bold">
                ({notifications.length})
              </span>
            )}
          </h2>
        </div>
        <div className="flex-1 overflow-y-auto px-3 py-4">
          {notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-40 text-center">
              <div className="h-8 w-8 rounded-full bg-white/5 flex items-center justify-center mb-2">
                <Bell className="h-4 w-4 text-muted-foreground/40" />
              </div>
              <p className="text-[11px] text-muted-foreground/40">{T.dashboard.noNotifications}</p>
            </div>
          ) : (
            <div className="space-y-2">
              {notifications.map(n => {
                const roleIcon = n.role ? roleOf(rosterCatalog, n.role)?.emoji : null
                const isRead = readNotifIds.has(n.id)
                const borderClass = isRead
                  ? 'border-white/12 bg-white/[0.035]'
                  : 'border-live/40 bg-live/[0.07]'

                // Determine notification type label, description, and sign-up status
                let typeLabel = ''
                let description = ''
                let showConfirmHint = false
                const isRosterType = n.notif_type === 'roster_posted' || n.notif_type === 'roster_change'

                // Check sign-up status (only for new_match)
                const matchEntry = matches.find(m => m.event_id === n.event_id)
                const eventEntry = events.find(e => e.id === n.event_id)
                const relevantSignUps = matchEntry?.event.sign_ups ?? eventEntry?.sign_ups
                const signedUp = !isRosterType && relevantSignUps && user ? relevantSignUps.some(s => s.user_id === user.id) : false

                if (n.notif_type === 'roster_posted') {
                  typeLabel = T.dashboard.rosterPlacement
                  description = n.role === 'substitute' ? T.dashboard.benchPlacementDesc : T.dashboard.rosterPlacementDesc
                  showConfirmHint = true
                } else if (n.notif_type === 'roster_change') {
                  typeLabel = T.dashboard.rosterUpdate
                  showConfirmHint = true
                  if (n.change_type === 'added') description = T.dashboard.rosterAddedDesc
                  else if (n.change_type === 'moved') description = T.dashboard.rosterMovedDesc
                  else if (n.change_type === 'removed') { description = T.dashboard.rosterRemovedDesc; showConfirmHint = false }
                  else description = T.dashboard.rosterUpdatedDesc
                } else if (n.notif_type === 'strategy_posted') {
                  // Opens the match, where the strategy card sits next to the roster.
                  typeLabel = T.dashboard.strategyPosted
                  description = n.strategy_name
                    ? T.dashboard.strategyPostedDesc.replace('{name}', n.strategy_name)
                    : ''
                } else if (n.notif_type === 'signup_reminder') {
                  typeLabel = T.dashboard.signupReminder
                  description = T.dashboard.signupReminderDesc
                } else {
                  typeLabel = T.dashboard.newMatch
                  description = signedUp ? '' : T.dashboard.notSignedUpYet
                }

                const navTarget = n.notif_type === 'new_match' || n.notif_type === 'strategy_posted' || n.notif_type === 'signup_reminder' || n.event_id.startsWith('match_post_') || matches.some(m => m.event_id === n.event_id)
                  ? '/matches' : '/events'

                return (
                  <div
                    key={n.id}
                    className={`cursor-pointer space-y-1.5 rounded-sm border ${borderClass} p-3 transition-colors`}
                    onClick={() => handleMarkRead(n.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5">
                        {n.created_at && (
                          <span className="text-[9px] text-muted-foreground/40">{timeAgo(n.created_at)}</span>
                        )}
                        {n.created_at && !isRead && <span className="text-[9px] text-muted-foreground/20">·</span>}
                        {!isRead && (
                          <span className="text-[9px] font-bold text-live animate-pulse">{T.dashboard.new}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={e => { e.stopPropagation(); navigate(`${navTarget}?select=${n.event_id}`) }}
                          className="text-muted-foreground/30 hover:text-muted-foreground transition-colors"
                          title={T.dashboard.goToEvent}
                        >
                          <ExternalLink className="h-3 w-3" />
                        </button>
                        <button
                          onClick={e => { e.stopPropagation(); handleRemoveNotif(n.id) }}
                          className="text-muted-foreground/30 hover:text-muted-foreground transition-colors"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    </div>
                    <p className="font-display text-[10px] font-bold uppercase tracking-[0.16em] text-live/80">{typeLabel}</p>
                    <p className="truncate font-display text-[13px] font-bold leading-tight tracking-tight">{n.event_title || 'Event'}</p>
                    {isRosterType && (
                      <div className="flex items-center gap-1.5">
                        {roleIcon && <EmojiGlyph emoji={roleIcon} size={16} />}
                        <span className="text-[11px] text-white/80">
                          {n.squad_name}
                          {/* A role key ('sl') means nothing to a reader; Settings has its name. */}
                          {n.role && n.role !== 'substitute' && <span className="text-white/60"> · {roleOf(rosterCatalog, n.role)?.name ?? n.role}</span>}
                        </span>
                      </div>
                    )}
                    {n.start_time && (
                      <p className="text-[10px] text-muted-foreground/35">{formatEventDate(n.start_time)}</p>
                    )}
                    {n.notif_type === 'new_match' && signedUp && (
                      <p className="text-[10px] font-medium text-green-400/70">
                        <Check className="inline h-3 w-3 -mt-0.5 mr-0.5" />{T.dashboard.signedUp}
                      </p>
                    )}
                    {description && <p className="text-[10px] text-live/60">{description}</p>}
                    {showConfirmHint && (
                      <button
                        onClick={e => { e.stopPropagation(); handleRemoveNotif(n.id); navigate(`${navTarget}?select=${n.event_id}`) }}
                        className="flex w-full items-center justify-center gap-1 rounded-sm border border-live/30 bg-live/10 px-2 py-1.5 text-[11px] font-bold tracking-wider text-live transition-colors hover:bg-live/20"
                      >
                        <Check className="h-3 w-3" />
                        {T.dashboard.confirmDecline}
                      </button>
                    )}
                    {n.notif_type === 'new_match' && !signedUp && (
                      <button
                        onClick={e => { e.stopPropagation(); handleRemoveNotif(n.id); navigate(`/matches?select=${n.event_id}`) }}
                        className="flex w-full items-center justify-center gap-1 rounded-sm border border-live/30 bg-live/10 px-2 py-1.5 text-[11px] font-bold tracking-wider text-live transition-colors hover:bg-live/20"
                      >
                        <UserPlus className="h-3 w-3" />
                        {T.dashboard.signUp}
                      </button>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </aside>
    </div>
  )
}
